import React, { useRef, useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import emailjs from 'emailjs-com';

import behance from '../../assets/navicon/behance.webp';
import insta from '../../assets/navicon/gram.webp';
import xcom from '../../assets/navicon/x.webp';
import pinterest from '../../assets/navicon/pin.webp';

// ── 1. Import Sound Controller & Audio Assets ──
import { sounds } from '../Utils/soundEffects';
import socialHoverSfx from '../../assets/sounds/node-hover.mp3';   // Subtle hover tick
import submitClickSfx from '../../assets/sounds/tap-main.mp3'; // Tactile click sound

import './../styles/Contact.css';

gsap.registerPlugin(ScrollTrigger);

// 5 Unsplash images for the floating upper card row
// const CARD_PHOTOS = [
//   { img: 'https://images.unsplash.com/photo-1534447677768-be436bb09401?q=80&w=600&auto=format&fit=crop', rot: -10, y: 15 },
//   { img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=600&auto=format&fit=crop', rot: -4, y: -8 },
//   { img: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?q=80&w=600&auto=format&fit=crop', rot: 1, y: 5 },
//   { img: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?q=80&w=600&auto=format&fit=crop', rot: 5, y: -6 },
//   { img: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?q=80&w=600&auto=format&fit=crop', rot: 10, y: 14 },
// ];

const SOCIAL_LINKS = [
  { icon: insta, lbl: 'Instagram', url: 'https://www.instagram.com/pixelarchive._/' },
  { icon: behance, lbl: 'Behance', url: 'https://www.behance.net/navyadhawde' },
  { icon: xcom, lbl: 'X', url: 'https://x.com/pixelsarchive_' },
  { icon: pinterest, lbl: 'Pinterest', url: 'https://in.pinterest.com/pixelsarchive/' },
];

const Contact = () => {
  const formRef = useRef();
  const [submitted, setSubmitted] = useState(false);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  // ── 2. Preload Sounds on Mount ──
  useEffect(() => {
    sounds.loadSound('socialHover', socialHoverSfx);
    sounds.loadSound('submitClick', submitClickSfx);

    const unlockAudio = () => {
      sounds.init();
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };

    window.addEventListener('pointerdown', unlockAudio);
    window.addEventListener('keydown', unlockAudio);

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  const triggerFireworks = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 45;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 6 + 3;
      particles.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: Math.random() * 2.5 + 2,
        alpha: 1,
        color: i % 2 === 0 ? '255, 182, 193' : '230, 190, 250',
      });
    }

    const renderLoop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1;
        p.alpha -= 0.03;

        if (p.alpha > 0) {
          alive = true;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${p.color}, ${p.alpha})`;
          ctx.fill();
        }
      });

      if (alive) requestAnimationFrame(renderLoop);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    requestAnimationFrame(renderLoop);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // 3. Play click sound right on submit
    sounds.play('submitClick', 0.4);
    emailjs.sendForm('service_47ge57h', 'template_k65yuv8', formRef.current, 'G1aM7NANkpElsZhcy')
      .then(() => {
        setSubmitted(true);
        triggerFireworks();
        setTimeout(() => setSubmitted(false), 3000);
      })
      .catch(() => {
        alert('Failed to send message, please try again.');
      });
    e.target.reset();
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 75%',
          end: 'bottom 20%',
          toggleActions: 'restart pause resume reverse',
        },
      });

      // Upper preview cards float in
      tl.fromTo(
        '.upper-photo-card',
        { opacity: 0, y: 70, scale: 0.9 },
        { opacity: 1, y: 0, scale: 1, duration: 0.2, stagger: 0.1, ease: 'power1.out' }
      )
      // Main dark container pop
      .fromTo(
        '.contact-dark-console',
        { opacity: 0, y: 40, scale: 0.98 },
        { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'power3.out' },
        '-=1.5'
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="contact" className="contact-root" ref={containerRef}>
      <canvas ref={canvasRef} className="contact-fireworks-canvas" />

      {/* ── 1. UPPER HORIZON CARDS ROW (Tilted Behind the Console) ── */}
      {/* <div className="contact-upper-cards">
        {CARD_PHOTOS.map((item, idx) => (
          <div
            key={idx}
            className="upper-photo-card"
            style={{
              transform: `rotate(${item.rot}deg) translateY(${item.y}px)`,
              zIndex: idx + 1,
            }}
          >
            <div className="card-glass-frame">
              <img src={item.img} alt="Visual Mood" className="card-media-img" loading="lazy" />
            </div>
          </div>
        ))}
      </div> */}

      {/* ── 2. DARK LIQUID GLASS CONSOLE CONTAINER ── */}
      <div className="contact-dark-console">
        <div className="console-inner-grid">
          
          {/* Left Column: Heading + Description + 4 Social Icons */}
          <div className="console-left-col">
            <h2 className="console-title">
              Let's <span className="title-talk-italic">Talk</span>
            </h2>
            <p className="console-subtext">
              exploring modern navigation systems through glassmorphism, motion, and layered interaction design
            </p>

            <div className="console-socials-row">
              {SOCIAL_LINKS.map((soc, i) => (
                <a
                  key={i}
                  href={soc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="console-social-btn"
                  aria-label={soc.lbl}
                  onMouseEnter={() => sounds.play('socialHover', 0.2)}
                >
                  <img src={soc.icon} alt={soc.lbl} />
                </a>
              ))}
            </div>
          </div>

          {/* Right Column: Liquid Inputs + Explore Submit Button */}
          <div className="console-right-col">
            <form className="console-liquid-form" ref={formRef} onSubmit={handleSubmit}>
              <div className="input-field-wrapper">
                <input
                  type="text"
                  name="from_name"
                  placeholder="Name"
                  required
                  className="liquid-input"
                />
              </div>

              <div className="input-field-wrapper">
                <input
                  type="email"
                  name="from_email"
                  placeholder="Email"
                  required
                  className="liquid-input"
                />
              </div>

              <div className="input-field-wrapper">
                <textarea
                  name="message"
                  placeholder="Message"
                  rows="3"
                  required
                  className="liquid-input liquid-textarea"
                />
              </div>

              <div className="form-submit-row">
                <button type="submit" className="console-explore-btn">
                  {submitted ? 'Sent!' : 'Connect'}
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;